import { CssSVGClasses } from "features/organizations/asset-sections/pages/editor/editor-utils/editor-types"
import { SvgHandler } from "features/organizations/asset-sections/pages/editor/helpers/svg-helpers"
import { ProvisionType } from "../../models/provision-models"
import { SelectedElement } from "../../provisions-utils/provision-types"

const findMatching = (elements: NodeListOf<Element>, id: string) => {
    return Array.from(elements).find(element => element.id === id)
}

const DEFAULT_LINER_REGION_SCALE = 5

const tmpBBox = (elem: SVGGraphicsElement) => {
    var bbox, tempDiv, tempSvg: SVGGraphicsElement
    if (document.contains(elem)) {
        return elem.getBBox()
    } else {
        tempDiv = document.createElement("div")
        tempDiv.setAttribute("style", "position:absolute; visibility:hidden; width:0; height:0")
        if (elem.tagName === "svg") {
            tempSvg = elem.cloneNode(true) as SVGGraphicsElement
        } else {
            tempSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
            tempSvg.appendChild(elem.cloneNode(true))
        }
        tempDiv.appendChild(tempSvg)
        document.body.appendChild(tempDiv)
        bbox = tempSvg.getBBox()
        document.body.removeChild(tempDiv)
        return bbox
    }
}

const provisionTypeClass = {
    [ProvisionType.Commissioned]: "ws-commissioned",
    [ProvisionType.Decommissioned]: "ws-decommissioned",
    [ProvisionType.Planned]: "ws-planned",
}

const addUiForHoleElement = (svgDocument: Document, sensorHole: SVGPathElement, scale?: number, id?: string, provisionType?: ProvisionType) => {
    scale = scale ?? DEFAULT_LINER_REGION_SCALE

    // Get the bounding box of the path
    const boundingBox = tmpBBox(sensorHole)

    //Calculate the center of the circle.
    const centerX = boundingBox.x + boundingBox.width / 2
    const centerY = boundingBox.y + boundingBox.height / 2

    // Calculate the diameter based on the bounding box dimensions
    const diameter = Math.max(boundingBox.width, boundingBox.height)
    const radius = diameter / 2

    sensorHole.setAttribute("fill", "none")
    sensorHole.setAttribute("stroke", "black")
    sensorHole.setAttribute("stroke-width", "0.5")

    const sensorLocation = svgDocument.createElementNS("http://www.w3.org/2000/svg", "circle") as SVGCircleElement
    sensorLocation.setAttribute("cx", centerX.toString())
    sensorLocation.setAttribute("cy", centerY.toString())
    sensorLocation.setAttribute("r", (radius + 5).toString())
    sensorLocation.setAttribute("stroke", "black")
    sensorLocation.classList.add("ws-sensor-location")

    if (id) {
        sensorLocation.setAttribute("id", id)
    }
    if (provisionType) {
        sensorLocation.classList.add(provisionTypeClass[provisionType])
    }

    sensorHole?.parentElement?.insertBefore(sensorLocation, sensorHole)
}

export const enrichProvisionSvg = (rawSVGImage: string, selectedElements: SelectedElement[], scale: number) => {
    const svgDocument = SvgHandler.Parse(rawSVGImage)
    let errors: string[] = []

    // Reset

    const resetStyles = [CssSVGClasses.WsLiner, CssSVGClasses.WsHole, CssSVGClasses.WsRegion]
    resetStyles.forEach(selector => [...svgDocument.getElementsByClassName(selector)].forEach(e => e.removeAttribute("style")))

    svgDocument.querySelectorAll(".ws-autopopulated").forEach(w => w.remove())
    svgDocument.querySelectorAll(".ws-svg-hidden").forEach(w => w.classList.remove("ws-svg-hidden"))

    const approvedLinerLocations = svgDocument.querySelectorAll(`path.${CssSVGClasses.WsLiner}`)
    const approvedHoleLocations = [...svgDocument.querySelectorAll(`path.${CssSVGClasses.WsHole}`)]

    approvedHoleLocations.forEach(hole => {
        const selectedSensorLocation = selectedElements.find(x => x.sensorSvgReference == hole.id)
        const selectedSensorId = selectedSensorLocation ? `sensor-${selectedSensorLocation.sensorSvgReference}` : undefined
        addUiForHoleElement(svgDocument, hole as SVGPathElement, scale, selectedSensorId, selectedSensorLocation?.provisionType)
    })

    selectedElements.forEach(element => {
        if (element.type === "Liner") {
            const elem = findMatching(approvedLinerLocations, element.linerSvgReference) as SVGElement
            if (elem) {
                elem.classList.add(provisionTypeClass[element.provisionType])
                elem.style.stroke = "black"
            }
        }
    })

    const assetSectionImage = SvgHandler.SerializeString(svgDocument)
    return { assetSectionImage, errors }
}
